import React, { useState, useEffect, useRef } from 'react';
import './apply-now.css';
import { Container, Form, Button, Row, Col, Modal } from 'react-bootstrap';
import axios from 'axios';
import ThankYou from './ThankYou';

const ApplyNow = () => {
  const [formData, setFormData] = useState({
    loanAmount: '',
    monthlyIncome: '',
    fullName: '',
    panNumber: '',
    dob: '',
    gender: '',
    mobile: '',
    altMobile: '',
    email: '',
    officialEmail: '',
    obligations: '',
    state: '',
    city: '',
    pincode: '',
    termsAccepted: false,
  });

  const [errors, setErrors] = useState({});
  const [panChecking, setPanChecking] = useState(false);
  const [panLookupMessage, setPanLookupMessage] = useState('');
  const [panReapplyBlocked, setPanReapplyBlocked] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState('');
  const [cityId, setCityId] = useState('');
  const [pincodeList, setPincodeList] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // ✅ New states for Selfie Capture
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState('');
  const [showCameraModal, setShowCameraModal] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastVerifiedPanRef = useRef('');

  useEffect(() => {
    axios.get('https://loansalary.in/Api/TaskApi/getState')
      .then(res => {
        if (res.data.Status === 1) {
          setStates(res.data.Data);
        } else {
          alert(res.data.Message || 'Failed to fetch states');
        }
      })
      .catch(err => {
        console.error('Failed fetching states:', err);
        alert('Could not load states');
      });
  }, []);

const handleChange = (e) => {
  const { id, value, type, checked } = e.target;

  let error = '';

  if ((id === 'mobile' || id === 'altMobile') && value.length > 0 && value.length !== 10) {
    error = 'Enter 10-digit number';
  }

  setFormData(prev => ({
    ...prev,
    [id]: type === 'checkbox' ? checked : value
  }));

  setErrors(prev => ({
    ...prev,
    [id]: error
  }));
};

const handleKeyPress = (e, fieldName) => {
  if (!/[0-9]/.test(e.key)) {
    e.preventDefault();
    setErrors(prev => ({
      ...prev,
      [fieldName]: 'Only digits are allowed',
    }));
  } else {
    // Clear the error if valid key is pressed
    setErrors(prev => ({
      ...prev,
      [fieldName]: '',
    }));
  }
};


  const handleStateChange = async (e) => {
    const sid = e.target.value;
    const selectedState = states.find(s => s.state_id.toString() === sid);

    setFormData(prev => ({ ...prev, state: selectedState?.state || '', city: '', pincode: '' }));
    setStateId(sid);
    setCityId('');
    setCities([]);
    setPincodeList([]);
    setErrors(prev => ({ ...prev, state: '', city: '', pincode: '' }));

    if (!sid) return;

    await loadCitiesForState(sid);
  };

  const handleCityChange = async (e) => {
    const cid = e.target.value;
    const selectedCity = cities.find(c => c.city_id.toString() === cid);

    setFormData(prev => ({
      ...prev,
      city: selectedCity?.city || '',
      pincode: ''
    }));
    setCityId(cid);
    setErrors(prev => ({ ...prev, city: '', pincode: '' }));

    await loadPincodesForCity(cid);
  };

  const loadCitiesForState = async (sid) => {
    if (!sid) {
      setCities([]);
      return;
    }

    try {
      const resp = await axios.post(
        'https://loansalary.in/Api/TaskApi/getCity',
        { state_id: sid },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (resp.data.Status === 1) {
        setCities(resp.data.Data);
      } else {
        setCities([]);
      }
    } catch (err) {
      console.error('Failed fetching cities:', err);
      setCities([]);
    }
  };

  const loadPincodesForCity = async (cid) => {
    if (!cid) {
      setPincodeList([]);
      return;
    }

    try {
      const resp = await axios.post(
        'https://loansalary.in/Api/TaskApi/getPincodeByCityId',
        { city_id: cid },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (resp.data.Status === 1) {
        setPincodeList(resp.data.Data);
      } else {
        setPincodeList([]);
      }
    } catch (err) {
      console.error('Failed fetching pincodes:', err);
      setPincodeList([]);
    }
  };

  const clearCustomerFetchedData = (currentPan) => {
    setFormData(prev => ({
      ...prev,
      panNumber: currentPan !== undefined ? currentPan : prev.panNumber,
      fullName: '',
      dob: '',
      gender: '',
      mobile: '',
      altMobile: '',
      email: '',
      officialEmail: '',
      obligations: '',
      state: '',
      city: '',
      pincode: '',
    }));
    setStateId('');
    setCityId('');
    setCities([]);
    setPincodeList([]);
    lastVerifiedPanRef.current = '';
    setPanReapplyBlocked(false);
  };

  const REAPPLY_ALLOWED_LOAN_STATUSES = ['CLOSED', 'REJECT', 'REJECTED'];

  const applyLoanStatusFeedback = (customerData) => {
    const loanStatus = customerData?.status
      ? String(customerData.status).toUpperCase()
      : '';

    if (!loanStatus) {
      setPanReapplyBlocked(false);
      setErrors(prev => ({ ...prev, panNumber: '' }));
      setPanLookupMessage('Customer details loaded successfully.');
      return;
    }

    if (REAPPLY_ALLOWED_LOAN_STATUSES.includes(loanStatus)) {
      setPanReapplyBlocked(false);
      setPanLookupMessage('');
      setErrors(prev => ({
        ...prev,
        panNumber: `Latest Status: ${loanStatus} (You can re-apply)`,
      }));
      return;
    }

    setPanReapplyBlocked(true);
    setPanLookupMessage('');
    setErrors(prev => ({
      ...prev,
      panNumber: `Your loan status is ${loanStatus}. Cannot re-apply.`,
    }));
  };

  const buildFullName = (data) =>
    [data.first_name, data.middle_name, data.sur_name]
      .filter(Boolean)
      .join(' ')
      .trim();

  const applyCustomerData = async (data) => {
    const genderValue = data.gender ? data.gender.toLowerCase() : '';

    setFormData(prev => ({
      ...prev,
      fullName: buildFullName(data),
      dob: data.dob || '',
      gender: ['male', 'female', 'other'].includes(genderValue) ? genderValue : '',
      mobile: data.mobile || '',
      altMobile: data.alternate_mobile || '',
      email: data.email || '',
      officialEmail: data.alternate_email || '',
      obligations: data.obligations ? String(data.obligations).replace(/\.00$/, '') : '',
      state: data.state_name || '',
      city: data.city_name || '',
      pincode: data.pincode || '',
    }));

    if (data.state_id) {
      const sid = String(data.state_id);
      setStateId(sid);
      await loadCitiesForState(sid);
    }

    if (data.city_id) {
      const cid = String(data.city_id);
      setCityId(cid);
      await loadPincodesForCity(cid);
    }
  };

  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase();

    if (value !== lastVerifiedPanRef.current) {
      clearCustomerFetchedData(value);
    } else {
      setFormData(prev => ({ ...prev, panNumber: value }));
    }

    setPanLookupMessage('');
    setPanReapplyBlocked(false);
    setErrors(prev => ({ ...prev, panNumber: '' }));

    if (value.length === 10) {
      checkPan(value);
    }
  };

  const isLoanBlockedResponse = (data) => {
    const msg = String(data?.message || '').toLowerCase();
    return (
      msg.includes('cannot re-apply') ||
      msg.includes('loan status is')
    );
  };

  const isCustomerNotFoundResponse = (data) => {
    if (!data || data.status === true || isLoanBlockedResponse(data)) return false;
    const msg = String(data.message || '').toLowerCase();
    return msg.includes('not found') || msg.includes('no customer');
  };

  const checkPan = async (panInput) => {
    const pan = String(panInput ?? formData.panNumber).trim().toUpperCase();
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (pan.length !== 10) {
      return;
    }

    if (!panRegex.test(pan)) {
      setPanLookupMessage('');
      setErrors(prev => ({
        ...prev,
        panNumber: 'Enter valid PAN number (e.g. ABCDE1234F)',
      }));
      return;
    }

    setPanChecking(true);
    setPanLookupMessage('');
    setPanReapplyBlocked(false);

    try {
      const [blacklistResp, customerResp] = await Promise.all([
        axios.get(
          `https://loansalary.in/Api/TaskApi/checkGetCustomerbyPanBlacklist?pancard=${encodeURIComponent(pan)}`
        ),
        axios.get(
          `https://loansalary.in/Api/TaskApi/checkGetCustomerbyPan?pancard=${encodeURIComponent(pan)}`
        ),
      ]);

      const blacklistData = blacklistResp.data;

      if (blacklistData?.blacklisted === true) {
        setPanReapplyBlocked(true);
        setPanLookupMessage('');
        setErrors(prev => ({
          ...prev,
          panNumber: blacklistData.message || 'Customer is blacklisted.',
        }));
        lastVerifiedPanRef.current = pan;
        return;
      }

      const resp = customerResp;

      if (resp.data?.status === true && resp.data?.data) {
        const customerData = resp.data.data;
        await applyCustomerData(customerData);
        lastVerifiedPanRef.current = pan;
        applyLoanStatusFeedback(customerData);
      } else if (isCustomerNotFoundResponse(resp.data)) {
        clearCustomerFetchedData(pan);
        lastVerifiedPanRef.current = pan;
        setErrors(prev => ({ ...prev, panNumber: '' }));
        setPanLookupMessage('No existing customer found. Please fill your details.');
      } else {
        setErrors(prev => ({
          ...prev,
          panNumber: resp.data?.message || 'Unable to fetch customer details.',
        }));
        setPanLookupMessage('');
      }
    } catch (err) {
      console.error('PAN check failed:', err);

      const status = err.response?.status;
      const responseData = err.response?.data;
      let message = '';

      if (typeof responseData === 'string' && responseData) {
        try {
          message = JSON.parse(responseData).message || responseData;
        } catch {
          message = responseData;
        }
      } else if (responseData?.message) {
        message = responseData.message;
      }

      if (status === 404 || isCustomerNotFoundResponse(responseData)) {
        clearCustomerFetchedData(pan);
        lastVerifiedPanRef.current = pan;
        setErrors(prev => ({ ...prev, panNumber: '' }));
        setPanLookupMessage('No existing customer found. Please fill your details.');
      } else {
        setErrors(prev => ({
          ...prev,
          panNumber: message || 'Unable to verify PAN. Please try again.',
        }));
        setPanLookupMessage('');
      }
    } finally {
      setPanChecking(false);
    }
  };

  // Functions for Selfie Capture
  const startCamera = async () => {
    setSelfieFile(null);
    setSelfiePreviewUrl('');
    setShowCameraModal(true);
    setErrors(prev => ({ ...prev, selfieFile: '' }));

    try {
      const constraints = { video: { facingMode: "user" } }; // 'user' for front camera (mobile)
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert('Camera access denied or device not found.');
      setShowCameraModal(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowCameraModal(false);
  };

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video stream
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      // Draw the current video frame onto the canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to a data URL (PNG)
      const dataUrl = canvas.toDataURL('image/png');
      setSelfiePreviewUrl(dataUrl);

      // Convert data URL to a File object
      canvas.toBlob(blob => {
        const file = new File([blob], "selfie.png", { type: "image/png" });
        setSelfieFile(file);
        setErrors(prev => ({ ...prev, selfieFile: '' }));
      }, 'image/png');
    }
    stopCamera(); // Close modal after capture
  };
// End functions for Selfie Capture

  const validate = () => {
    const newErrors = {};
    const onlyDigits = /^\d+$/;

    if (!formData.loanAmount) newErrors.loanAmount = 'Required';
    if (!formData.monthlyIncome) newErrors.monthlyIncome = 'Required';
    if (!formData.fullName) newErrors.fullName = 'Required';
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!formData.panNumber) {
      newErrors.panNumber = 'Required';
    } else if (!panRegex.test(formData.panNumber.trim().toUpperCase())) {
      newErrors.panNumber = 'Enter valid PAN number (e.g. ABCDE1234F)';
    }
    if (!formData.dob) newErrors.dob = 'Required';
    if (!formData.gender) newErrors.gender = 'Required';

    if (!formData.mobile) {
      newErrors.mobile = 'Required';
    } else if (!onlyDigits.test(formData.mobile) || formData.mobile.length !== 10) {
      newErrors.mobile = 'Enter valid 10-digit mobile number';
    }

    if (formData.altMobile && (!onlyDigits.test(formData.altMobile) || formData.altMobile.length !== 10)) {
      newErrors.altMobile = 'Enter valid 10-digit mobile number';
    }

    if (!formData.email) {
      newErrors.email = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter valid email address';
    }

    if (formData.obligations && !onlyDigits.test(formData.obligations)) {
      newErrors.obligations = 'Obligations must be numeric';
    }

    if (!formData.state) newErrors.state = 'Required';
    if (!formData.city) newErrors.city = 'Required';
    if (!formData.pincode) newErrors.pincode = 'Required';

    if (!formData.termsAccepted) newErrors.termsAccepted = 'Accept terms';

    if (panReapplyBlocked) {
      newErrors.panNumber =
        errors.panNumber || 'Your loan is in progress. Cannot re-apply.';
    }

    // Selfie validation - commented out since UI is disabled
    // if (!selfieFile) {
    //     newErrors.selfieFile = 'Selfie is required';
    // }
    // End new validations

    return newErrors;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submit clicked, validating...');
    console.log('Form Data:', formData);
    const formErrors = validate();
    console.log('Validation errors:', formErrors);
    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      // Show alert with missing fields
      const missingFields = Object.keys(formErrors).join(', ');
      alert('Please fill required fields: ' + missingFields);
      console.log('Form has errors, stopping submission');
      return;
    }
    console.log('Validation passed, submitting...');

    // NOTE: You need to implement the actual logic to upload the 'selfieFile' 
    // to your server and get the URL before proceeding.
    // For now, I'm using a placeholder 'TBD_SELFIE_URL'.
    const selfieImageUrl = selfieFile ? 'TBD_SELFIE_URL' : null; 
    
    if (!selfieImageUrl) {
      // If you implement upload logic here, handle its failure too.
    }


    const payload = {
      loan_amount: formData.loanAmount,
      monthly_income: formData.monthlyIncome,
      first_name: formData.fullName,
      middle_name: '',
      sur_name: '',
      pan: formData.panNumber,
      dob: formData.dob,
      gender: formData.gender.toUpperCase(),
      mobile: formData.mobile,
      alternate_mobile: formData.altMobile || '',
      otp: 1234,
      email_personal: formData.email,
      email_official: formData.officialEmail || '',
      obligations: formData.obligations || 0,
      state_id: stateId,
      city: cityId,
      pin: formData.pincode,
      term_and_condition: formData.termsAccepted ? 'YES' : 'NO',
      ip: '106.219.215.60',
      coordinates: '',
      source: 'Loansalary',
      lead_data_source_id: 38,
      utm_source: '',
      utm_campaign: '',
      selfie_image_url: selfieImageUrl || '',
    };

    try {
      console.log('Submitting payload:', payload);
      
      const resp = await axios.post(
        'https://loansalary.in/Api/TaskApi/saveLendingPage',
        payload,
        { 
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000 // 30 second timeout
        }
      );

      console.log('API Response:', resp.data);

      if (resp.data.Status === 1) {
        setFormSubmitted(true);
      } else {
        // Show detailed error message from API
        const message = resp?.data?.Message || 'Submit failed';
        const plainTextMessage = typeof message === 'string'
          ? message.replace(/<[^>]+>/g, '\n').trim()
          : 'Submit failed';
        console.error('API Error:', resp.data);
        alert(`Error: ${plainTextMessage}`);
      }
    } catch (err) {
      console.error('Submit error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      
      let errorMessage = 'Submit failed. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        errorMessage = `Server Error (${err.response.status}): ${err.response.data?.Message || 'Unknown error'}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
      } else {
        // Something else happened
        errorMessage = `Error: ${err.message}`;
      }
      
      alert(errorMessage);
    }

  };

  if (formSubmitted) {
    return <ThankYou />;
  }

  return (
    <div className="apply-now-wrapper">
      <Container className="apply-now-form-container">
        <h2 className="form-heading">Apply Now</h2>
        <Form onSubmit={handleSubmit} noValidate>

          <Row>
            <Col md={6}>
              <Form.Group controlId="panNumber" className="mb-3">
                <Form.Label>PAN Number <span className="required">*</span></Form.Label>
                <Form.Control
                  type="text"
                  maxLength="10"
                  value={formData.panNumber}
                  onChange={handlePanChange}
                  isInvalid={!!errors.panNumber}
                  disabled={panChecking}
                />
                {panChecking && (
                  <Form.Text className="pan-checking-text">Checking PAN...</Form.Text>
                )}
                {!panChecking && panLookupMessage && !errors.panNumber && (
                  <Form.Text className="pan-lookup-message">{panLookupMessage}</Form.Text>
                )}
                {!panChecking && errors.panNumber && (
                  <Form.Text
                    className={
                      errors.panNumber.includes('You can re-apply')
                        ? 'pan-lookup-warn'
                        : 'pan-lookup-error'
                    }
                  >
                    {errors.panNumber}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="loanAmount" className="mb-3">
                <Form.Label>Loan Amount <span className="required">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.loanAmount}
                  onChange={handleChange}
                  isInvalid={!!errors.loanAmount}
                />
                <Form.Control.Feedback type="invalid">{errors.loanAmount}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="monthlyIncome" className="mb-3">
                <Form.Label>Monthly Income <span className="required">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.monthlyIncome}
                  onChange={handleChange}
                  isInvalid={!!errors.monthlyIncome}
                />
                <Form.Control.Feedback type="invalid">{errors.monthlyIncome}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="fullName" className="mb-3">
                <Form.Label>Full Name <span className="required">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.fullName}
                  onChange={handleChange}
                  isInvalid={!!errors.fullName}
                />
                <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="dob" className="mb-3">
                <Form.Label>Date of Birth <span className="required">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  isInvalid={!!errors.dob}
                />
                <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="gender" className="mb-3">
                <Form.Label>Gender <span className="required">*</span></Form.Label>
                <Form.Select
                  value={formData.gender}
                  onChange={handleChange}
                  isInvalid={!!errors.gender}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="mobile" className="mb-3">
                <Form.Label>Mobile <span className="required">*</span></Form.Label>
                <Form.Control
                  type="tel"
                  maxLength="10"
                  value={formData.mobile}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, 'mobile')}
                  isInvalid={!!errors.mobile}
                />
                {errors.mobile && <div style={{ color: 'red', fontSize: '0.875em' }}>{errors.mobile}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="altMobile" className="mb-3">
                <Form.Label>Alternate Mobile</Form.Label>
                <Form.Control
                  type="tel"
                  maxLength="10"
                  value={formData.altMobile}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, 'altMobile')}
                  isInvalid={!!errors.altMobile}
                />
                {errors.altMobile && <div style={{ color: 'red', fontSize: '0.875em' }}>{errors.altMobile}</div>}
              </Form.Group>
            </Col>
          </Row>

          {/* Contact Details - 2 per row */}
          <Row>
            <Col md={6}>
              <Form.Group controlId="email" className="mb-3">
                <Form.Label>Email <span className="required">*</span></Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="officialEmail" className="mb-3">
                <Form.Label>Official Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.officialEmail}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="obligations" className="mb-3">
                <Form.Label>Obligations</Form.Label>
                <Form.Control
                  type="text"
                  maxLength="7"
                  value={formData.obligations}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, 'obligations')}
                  isInvalid={!!errors.obligations}
                />
                {errors.obligations && <div style={{ color: 'red', fontSize: '0.875em' }}>{errors.obligations}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="state" className="mb-3">
                <Form.Label>State <span className="required">*</span></Form.Label>
                <Form.Select value={stateId} onChange={handleStateChange} isInvalid={!!errors.state}>
                  <option value="">Select State</option>
                  {states.map(s => (
                    <option key={s.state_id} value={s.state_id}>
                      {s.state}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group controlId="city">
                <Form.Label>City <span className="required">*</span></Form.Label>
                <Form.Select value={cityId} onChange={handleCityChange} isInvalid={!!errors.city}>
                  <option value="">Select City</option>
                  {cities.map(city => (
                    <option key={city.city_id} value={city.city_id}>
                      {city.city}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="pincode">
                <Form.Label>Pincode <span className="required">*</span></Form.Label>
                <Form.Select
                  value={formData.pincode}
                  onChange={handleChange}
                  isInvalid={!!errors.pincode}
                  disabled={!cityId || pincodeList.length === 0}
                >
                  <option value="">Select Pincode</option>
                  {pincodeList.map(pin => (
                    <option key={pin.m_pincode_id} value={pin.m_pincode_value}>
                      {pin.m_pincode_value}
                    </option>
                  ))}
                </Form.Select>
                {errors.pincode && <div className="text-danger">{errors.pincode}</div>}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
                {/* Selfie Capture Section */}
              {/* <Form.Group controlId="selfieFile" className="mb-3">
                <Form.Label>Take a Live Selfie <span className="required">*</span></Form.Label>
               <Button
  variant={selfieFile ? "success" : "primary"}
  onClick={startCamera}
  className={`w-100 custom-start-btn`}
>
                    {selfieFile ? 'Selfie Captured (Retake)' : 'Capture Selfie'}
                </Button>
                 {selfiePreviewUrl && (
                    <div className="mt-2 text-center">
                        <img src={selfiePreviewUrl} alt="Selfie Preview" style={{ maxWidth: '100px', maxHeight: '100px', border: '1px solid #ccc', borderRadius: '5px' }} />
                    </div>
                )}
                {errors.selfieFile && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.selfieFile}</div>}
              </Form.Group> */}
            </Col>
            <Col md={6}>
              {/* Empty column for balance */}
            </Col>
          </Row>

          <Form.Group controlId="termsAccepted" className="mb-3 terms-check">
            <Form.Check
              type="checkbox"
              label={<><a href="/terms-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> <span className="required">*</span></>}
              checked={formData.termsAccepted}
              onChange={handleChange}
              isInvalid={!!errors.termsAccepted}
              feedback={errors.termsAccepted}
            />
            {errors.termsAccepted && <div style={{ color: 'red', fontSize: '0.875em' }}>{errors.termsAccepted}</div>}
          </Form.Group>

          <div className="mb-3 declaration-text">
            <p className="text-white small">
              <strong>I hereby declare that all the above information provided by me is true, correct, and verified to the best of my knowledge.</strong>
            </p>
          </div>

          <Button
            type="submit"
            className="apply-submit-btn"
            block
            disabled={panReapplyBlocked}
          >
            Submit
          </Button>
        </Form>
      </Container>
      
      {/* Selfie Capture Modal (Existing) */}
      <Modal show={showCameraModal} onHide={stopCamera} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Capture Selfie</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <video ref={videoRef} autoPlay style={{ width: '100%', maxWidth: '400px', display: selfiePreviewUrl ? 'none' : 'block' }}></video>
          {/* Canvas is hidden, used for processing the image */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
          <p className="mt-3">Please take a selfie with any Indian government ID (Aadhaar, PAN, voter ID, etc.)</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={stopCamera}>
            Cancel
          </Button>
          <Button variant="primary" onClick={captureSelfie} disabled={!videoRef.current}>
            Take Photo
          </Button>
        </Modal.Footer>
      </Modal>
      {/* End Selfie Capture Modal */}

    </div>
  );
};

export default ApplyNow;